import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { DateUtilsService } from '../../../../services/date-utils.service';
import { TimezoneService } from '../../../../services/timezone.service';
import { TourServiceAbsence } from '../../../../db-models/tour-dao';

interface DialogData {
  serviceName: string;
  absences: TourServiceAbsence[];
  lang: string;
  badgeLabel?: string;
}

@Component({
  selector: 'app-tour-upcoming-absences-dialog',
  templateUrl: './tour-upcoming-absences-dialog.component.html',
  styleUrls: ['./tour-upcoming-absences-dialog.component.scss'],
  imports: [DatePipe, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TourUpcomingAbsencesDialogComponent {
  private dialogRef = inject(MatDialogRef<TourUpcomingAbsencesDialogComponent>);
  private data = inject<DialogData>(MAT_DIALOG_DATA);
  private timezoneService = inject(TimezoneService);
  private dateUtilsService = inject(DateUtilsService);

  readonly serviceName = signal<string>(this.data.serviceName);
  readonly lang = signal<string>(this.data.lang || 'en');
  readonly badgeLabel = signal<string | undefined>(this.data.badgeLabel);

  /** Customer's IANA timezone — render absence dates/times in the user's local view,
   *  matching the widget's slot/cart pattern (always user-tz, no TZ label). */
  readonly userTimezone = this.timezoneService.getLocalTimezone();

  /** 12h/24h from the partner's time-format setting (AUTO → browser locale), same as every
   *  other time label. A symbolic format ('shortTime') would follow the language instead. */
  protected get timeFormat(): string {
    return this.dateUtilsService.userTimeFormat.date;
  }

  readonly absences = computed(() =>
    [...this.data.absences].sort((a, b) =>
      new Date(a.from_date_utc ?? a.from_date).getTime()
      - new Date(b.from_date_utc ?? b.from_date).getTime()
    )
  );

  isSameDay(absence: TourServiceAbsence): boolean {
    const from = new Date(absence.from_date_utc ?? absence.from_date);
    const to = new Date(absence.to_date_utc ?? absence.to_date);
    return from.toDateString() === to.toDateString();
  }

  /**
   * True when `all_day` is set OR the times sit on day boundaries
   * (start at 00:00, end at 23:55+). Some BE absences arrive with
   * `all_day=false` but full-day times (e.g. a week-long maintenance
   * window stored as 12:00 AM → 11:55 PM); rendering "12:00 AM →
   * 11:55 PM" alongside the dates reads as noise. Treat as all-day.
   */
  isEffectivelyAllDay(absence: TourServiceAbsence): boolean {
    if (absence.all_day) return true;
    const from = new Date(absence.from_date_utc ?? absence.from_date);
    const to = new Date(absence.to_date_utc ?? absence.to_date);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return false;
    const startsAtDayStart = from.getHours() === 0 && from.getMinutes() === 0;
    const endsAtDayEnd = to.getHours() === 23 && to.getMinutes() >= 55;
    return startsAtDayStart && endsAtDayEnd;
  }

  close(): void {
    this.dialogRef.close();
  }
}
